import { useRescheduleActivitySale } from 'api-hooks/sale/activity';
import { SaleDetailModel } from 'api-hooks/sale/model';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useNavigation from 'hooks/use-navigation';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import {
  FormContent,
  FormDivider,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  ActivityRescheduleFormType,
  ActivityRescheduleSchema,
  ActivitySaleAddOnsType,
  ActivitySaleGuestDetailType,
  ActivitySaleGuestType,
  ActivitySaleType,
} from './form-type';
import useGetDefaultValues from './hooks/use-get-default-values';
import ActivityItems from './items';
import SummaryActivityReschedule from './summary';
import { totalActivityRescheduleCalculation } from './total-calculation';
import RescheduleHistoryTable from '../reschedule-history-table';

interface Props {
  activitySale: SaleDetailModel;
}

export default function ActivityRescheduleForm(props: Props) {
  const { t } = useTranslation();
  const { activitySale } = props;
  const { navigate, close } = useNavigation();

  const { mutateAsync } = useRescheduleActivitySale();

  const defaultValues = useGetDefaultValues({ activitySale });

  const methods = useForm<ActivityRescheduleFormType>({
    defaultValues,
    resolver: useYupValidationResolver(ActivityRescheduleSchema),
    mode: 'onChange',
  });

  const onCancel = () => {
    close();
    navigate(NavigationRoutes.ActivitySalesView, {
      id: activitySale.id,
    });
  };

  const onSubmit = async (values: ActivityRescheduleFormType) => {
    try {
      values.total = totalActivityRescheduleCalculation(values.activitySales);
      const activitySales: ActivitySaleType[] = values.activitySales
        .map((as) => {
          const activitySaleAddOns: ActivitySaleAddOnsType[] =
            as.activitySaleAddOns
              .filter((asao) => asao.isSelected)
              .map((asao) => ({ ...asao, isSelected: undefined }));

          const activitySaleGuests: ActivitySaleGuestType[] =
            as.activitySaleGuests
              .filter((asao) => asao.isSelected)
              .map((asao) => {
                const activitySaleGuestDetails: ActivitySaleGuestDetailType[] =
                  asao.activitySaleGuestDetails
                    .filter((asgd) => asgd.isSelected)
                    .map((asgd) => ({ ...asgd, isSelected: undefined }));
                return {
                  ...asao,
                  isSelected: undefined,
                  activitySaleGuestDetails,
                };
              });

          return {
            id: as.id,
            isGroupedPrice: as.isGroupedPrice,
            activitySaleAddOns,
            activitySaleGuests,
          };
        })
        .filter((as) => as.activitySaleGuests.length);

      if (
        !activitySales.length ||
        !activitySales[0].activitySaleGuests[0].activitySaleGuestDetails.length
      ) {
        notification.error({ message: t('error:at_least_select_one_item') });
        return;
      }

      await mutateAsync({
        activitySaleId: activitySale.id,
        body: { ...values, activitySales },
      });
      onCancel();
    } catch (e) {
      notification.error({ message: e.message });
    }
  };

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      noReset
    >
      <FormContent>
        <FormHeader
          title={t('reschedule:reschedule_other')}
          data={activitySale}
          noDelete
          onCancel={onCancel}
        />
        <div>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="invoiceNumber"
                label={t('common:invoice_number')}
                disabled
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text"
                name="customer"
                label={t('common:customer')}
                disabled
              />
            </HalfContainer>
          </SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="transactionAt"
              label={t('common:transaction_at')}
              clearable={false}
              required
            />
          </HalfContainer>
          <Separator gap={32} direction="vertical">
            <RescheduleHistoryTable type="activity" saleId={activitySale.id} />
          </Separator>

          <Separator gap={16} direction="vertical">
            <ActivityItems />
          </Separator>
          <FormDivider />
        </div>
      </FormContent>
      <SummaryActivityReschedule />
    </Form>
  );
}
